/**
 * Feature Menu Integration
 *
 * Helpers to convert the feature tree returned by the app-feature endpoint into
 * the existing menu item structure used by `Menus/index.jsx`.
 */

/**
 * Normalize the `roles` field from the feature node into the `access` array
 * expected by the menu system.
 *
 * @param {string | string[] | null | undefined} roles
 * @returns {string[] | undefined}
 */
export const normalizeRolesToAccess = (roles) => {
  if (!roles) return undefined;

  if (Array.isArray(roles)) {
    const cleaned = roles.filter(Boolean);
    return cleaned.length ? cleaned : undefined;
  }

  if (typeof roles === 'string') {
    const cleaned = roles
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean);
    return cleaned.length ? cleaned : undefined;
  }

  return undefined;
};

/**
 * Convert a single feature node into a menu item.
 * Only includes the node if it is selected or has at least one selected child.
 *
 * @param {Object} feature
 * @param {Object} options
 * @param {(feature: Object) => string} [options.resolveLabel] - label resolver with i18n fallbacks
 * @param {Object.<string, any>} [options.iconMap] - mapping from feature code to icon component
 * @returns {Object | null}
 */
export const mapFeatureNodeToMenuItem = (feature, options = {}) => {
  if (!feature) return null;

  const { resolveLabel, iconMap } = options;

  const rawCode = feature.code || '';
  const hasHashPrefix = rawCode.startsWith('#');
  const codeForKey = hasHashPrefix ? rawCode.slice(1) : rawCode;
  const key = codeForKey || feature.id;

  if (!key) {
    return null;
  }

  let children;
  if (Array.isArray(feature.children) && feature.children.length > 0) {
    children = feature.children
      .map((child) => mapFeatureNodeToMenuItem(child, options))
      .filter((child) => child !== null)
      .sort((a, b) => {
        const seqA = a?.sequence ?? 0;
        const seqB = b?.sequence ?? 0;
        return seqA - seqB;
      });
  }

  const hasSelectedChildren = Array.isArray(children) && children.length > 0;
  const isSelected = feature.selected === true;

  if (!isSelected && !hasSelectedChildren) {
    return null;
  }

  const label = resolveLabel ? resolveLabel(feature) : feature.name || codeForKey;
  const access = normalizeRolesToAccess(feature.roles);

  // Special rule: keep `pengaturan` as a single sidebar menu entry.
  // Even if the API returns children (e.g. pengaturan/sekolah, pengaturan/kelas),
  // those sub-features are handled inside the `/pengaturan` section UI (cards),
  // not as a sidebar submenu.
  const collapseToSingleMenu = codeForKey === 'pengaturan';

  const isContainer = hasHashPrefix || !codeForKey || (!collapseToSingleMenu && hasSelectedChildren);
  const path = isContainer ? null : codeForKey;

  const icon = iconMap ? iconMap[codeForKey] ?? (key ? iconMap[key] : undefined) : undefined;

  return {
    id: key,
    name: label,
    icon,
    path,
    access,
    sequence: typeof feature.sort_order === 'number' ? feature.sort_order : 0,
    children: collapseToSingleMenu ? undefined : children,
  };
};

/**
 * Map an array of feature nodes to the menu item structure.
 *
 * @param {Array} features
 * @param {Object} options
 * @returns {Array}
 */
export const mapFeaturesToMenuItems = (features, options = {}) => {
  if (!Array.isArray(features)) return [];

  return features
    .map((feature) => mapFeatureNodeToMenuItem(feature, options))
    .filter((item) => item !== null)
    .sort((a, b) => {
      const seqA = a?.sequence ?? 0;
      const seqB = b?.sequence ?? 0;
      return seqA - seqB;
    });
};

export default {
  normalizeRolesToAccess,
  mapFeatureNodeToMenuItem,
  mapFeaturesToMenuItems,
};
